import type { QueryResponse, VisualizationType } from "@unbox/api";

const CATEGORY_CAP = 12;
const LINE_CAP = 400;
const NEEDS_CATEGORY = new Set<VisualizationType>(["bar", "line", "stacked_bar", "funnel", "list", "table", "videos"]);
const ROW_FORMS = new Set<VisualizationType>(["list", "table", "videos"]);
const ROW_FORM_CAP: Partial<Record<VisualizationType, number>> = { list: 10, table: 10, videos: 4 };
const ROW_FORM_VALUES: Partial<Record<VisualizationType, number>> = { list: 3, table: 4, videos: 1 };

export function isChartType(type: VisualizationType): boolean {
  return !ROW_FORMS.has(type);
}
const ACRONYMS: Record<string, string> = { gmv: "GMV", gpm: "GPM", roi: "ROI", ctr: "CTR", cpm: "CPM", sku: "SKU" };

export type ResolvedVisualization = {
  type: VisualizationType;
  title?: string;
  categoryColumn: string | null;
  detailColumn: string | null;
  valueColumns: string[];
  valueFormat: "currency" | "percent" | "number";
  unit: string;
  columns: string[];
  rows: Record<string, unknown>[];
};

export function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v.replace(/[, ]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function pctChange(first: number, last: number): number | null {
  if (!Number.isFinite(first) || first === 0) return null;
  return ((last - first) / Math.abs(first)) * 100;
}

export function numericSeries(rows: Record<string, unknown>[], col: string): number[] {
  return rows.map((r) => toNumber(r[col])).filter((n): n is number => n !== null);
}

export function prettifyColumn(col: string): string {
  return col
    .split("_")
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function formatChartValue(
  value: unknown,
  format: "currency" | "percent" | "number",
  unit: string,
  compact = false,
): string {
  const n = toNumber(value);
  if (n === null) return "No data";
  if (format === "percent") return `${n.toFixed(1)}%`;
  const sym = format === "currency" ? unit : "";
  if (compact && Math.abs(n) >= 10000) {
    const abs = Math.abs(n);
    const div = abs >= 1_000_000 ? 1_000_000 : 1000;
    const suffix = abs >= 1_000_000 ? "m" : "k";
    return `${sym}${(n / div).toFixed(1).replace(/\.0$/, "")}${suffix}`;
  }
  return `${sym}${Math.round(n).toLocaleString("en-GB")}`;
}

export function selectValidVisualizations(
  response: Pick<QueryResponse, "steps" | "visualizations">,
): ResolvedVisualization[] {
  const specs = response.visualizations ?? [];
  const out: ResolvedVisualization[] = [];
  for (const v of specs) {
    const step = response.steps?.find((s) => s.step === v.step);
    if (!step || step.error || !step.rows || step.rows.length === 0) continue;
    const cols = step.columns ?? [];
    const valueColumns = (v.value_columns ?? []).filter((c) => cols.includes(c));
    if (valueColumns.length === 0) continue;
    const catCol = v.category_column && cols.includes(v.category_column) ? v.category_column : null;
    if (NEEDS_CATEGORY.has(v.type) && !catCol) continue;
    const rowForm = ROW_FORMS.has(v.type);
    if (!rowForm && !step.rows.some((r) => toNumber(r[valueColumns[0]]) !== null)) continue;
    const detailCol = v.detail_column && cols.includes(v.detail_column) ? v.detail_column : null;
    // A line is a single time series: one row per x value. Duplicate category
    // values mean multiple series were concatenated (e.g. cohort × month),
    // which renders as a meaningless sawtooth — drop it rather than show garbage.
    if (v.type === "line" && catCol) {
      const cats = step.rows.map((r) => String(r[catCol]));
      if (new Set(cats).size < cats.length) continue;
    }
    const isMetric = v.type === "metric_strip" || v.type === "metric_grid";
    const cap = rowForm
      ? (ROW_FORM_CAP[v.type] ?? CATEGORY_CAP)
      : v.type === "line"
        ? LINE_CAP
        : isMetric
          ? step.rows.length
          : CATEGORY_CAP;
    out.push({
      type: v.type,
      title: v.title,
      categoryColumn: catCol,
      detailColumn: detailCol,
      valueColumns: rowForm
        ? valueColumns.slice(0, ROW_FORM_VALUES[v.type] ?? 1)
        : isMetric
          ? valueColumns.slice(0, 3)
          : [valueColumns[0]],
      valueFormat: v.value_format,
      unit: v.unit,
      columns: cols,
      rows: step.rows.slice(0, cap),
    });
    if (out.length === 1) break;
  }
  return out;
}
